/**
 * SourceGenerator Service
 * API integration for source generator CRUD operations
 *
 * @version 2025.1.0
 * @description Serviço de integração com API de fontes geradoras
 */
import apiClient from './apiClient';
import {
  SourceGenerator,
  SourceGeneratorFilters,
  SourceGeneratorFormData,
  SourceGeneratorApiPayload,
  SourceGeneratorListResponse,
  SourceGeneratorDependencies,
  SourceGeneratorRiskType,
  MaintenanceAlert,
} from '../types/sourceGenerator';

// Re-export types for convenience
export * from '../types/sourceGenerator';

export interface SourceGeneratorListParams extends SourceGeneratorFilters {
  page?: number;
  limit?: number;
}

class SourceGeneratorService {
  private baseUrl = '/source-generators';

  /**
   * Convert form data to API payload
   */
  private formDataToApiPayload(formData: SourceGeneratorFormData): SourceGeneratorApiPayload {
    return {
      name: formData.name,
      location: formData.location,
      riskType: formData.riskType,
      description: formData.description,
      manufacturer: formData.manufacturer ?? undefined,
      model: formData.model ?? undefined,
      serialNumber: formData.serialNumber ?? undefined,
      yearOfManufacture: formData.yearOfManufacture ?? null,
      maintenanceFrequency: formData.maintenanceFrequency ?? undefined,
      lastMaintenanceDate: formData.lastMaintenanceDate ?? null,
      nextMaintenanceDate: formData.nextMaintenanceDate ?? null,
      observations: formData.observations ?? undefined,
      status: formData.status,
    };
  }

  /**
   * Convert API response to form data
   */
  apiResponseToFormData(apiData: SourceGenerator): Partial<SourceGeneratorFormData> {
    return {
      name: apiData.name,
      location: apiData.location,
      riskType: apiData.riskType,
      description: apiData.description,
      manufacturer: apiData.manufacturer ?? undefined,
      model: apiData.model ?? undefined,
      serialNumber: apiData.serialNumber ?? undefined,
      yearOfManufacture: apiData.yearOfManufacture ?? null,
      maintenanceFrequency: apiData.maintenanceFrequency ?? undefined,
      lastMaintenanceDate: apiData.lastMaintenanceDate ?? null,
      nextMaintenanceDate: apiData.nextMaintenanceDate ?? null,
      observations: apiData.observations ?? undefined,
      status: apiData.status,
    };
  }

  /**
   * Get paginated list of source generators
   */
  async getSourceGenerators(params: SourceGeneratorListParams = {}): Promise<SourceGeneratorListResponse> {
    const { page = 1, limit = 20, ...filters } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (filters.search) queryParams.append('search', filters.search);
    if (filters.riskType) queryParams.append('riskType', filters.riskType);
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.location) queryParams.append('location', filters.location);
    if (filters.maintenanceOverdue !== undefined) queryParams.append('maintenanceOverdue', String(filters.maintenanceOverdue));
    if (filters.maintenanceDueSoon !== undefined) queryParams.append('maintenanceDueSoon', String(filters.maintenanceDueSoon));
    if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
    if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);

    const response = await apiClient.get(`${this.baseUrl}?${queryParams.toString()}`);

    // Backend returns: { data: { sourceGenerators, total, page, limit, totalPages } }
    const backendData = response.data.data || response.data;

    return {
      sourceGenerators: backendData.sourceGenerators ?? [],
      total: backendData.total ?? 0,
      page: backendData.page || page,
      limit: backendData.limit || limit,
      totalPages: backendData.totalPages || Math.ceil((backendData.total ?? 0) / limit),
    };
  }

  /**
   * Get source generator by ID
   */
  async getSourceGeneratorById(id: string | number): Promise<SourceGenerator> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data.data || response.data;
  }

  /**
   * Create new source generator
   */
  async createSourceGenerator(data: SourceGeneratorFormData): Promise<SourceGenerator> {
    const payload = this.formDataToApiPayload(data);
    const response = await apiClient.post(this.baseUrl, payload);
    return response.data.data || response.data;
  }

  /**
   * Update source generator by ID
   */
  async updateSourceGenerator(id: string | number, data: Partial<SourceGeneratorFormData>): Promise<SourceGenerator> {
    const payload = this.formDataToApiPayload(data as SourceGeneratorFormData);
    const response = await apiClient.put(`${this.baseUrl}/${id}`, payload);
    return response.data.data || response.data;
  }

  /**
   * Delete source generator by ID (soft delete)
   */
  async deleteSourceGenerator(id: string | number): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Activate source generator
   */
  async activateSourceGenerator(id: string | number): Promise<SourceGenerator> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/activate`);
    return response.data.data || response.data;
  }

  /**
   * Deactivate source generator
   */
  async deactivateSourceGenerator(id: string | number): Promise<SourceGenerator> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/deactivate`);
    return response.data.data || response.data;
  }

  /**
   * Set source generator to maintenance status
   */
  async setMaintenance(id: string | number): Promise<SourceGenerator> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/maintenance`);
    return response.data.data || response.data;
  }

  /**
   * Decommission source generator
   */
  async decommission(id: string | number): Promise<SourceGenerator> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/decommission`);
    return response.data.data || response.data;
  }

  /**
   * Record maintenance performed
   */
  async recordMaintenance(
    id: string | number,
    maintenanceDate: string,
    nextMaintenanceDate?: string
  ): Promise<SourceGenerator> {
    const response = await apiClient.post(`${this.baseUrl}/${id}/record-maintenance`, {
      maintenanceDate,
      nextMaintenanceDate,
    });
    return response.data.data || response.data;
  }

  /**
   * Get source generators by risk type
   */
  async getByRiskType(riskType: SourceGeneratorRiskType): Promise<SourceGenerator[]> {
    const response = await apiClient.get(`${this.baseUrl}/risk-type/${riskType}`);
    return response.data.data || response.data;
  }

  /**
   * Get maintenance alerts (global)
   */
  async getMaintenanceAlerts(daysAhead: number = 30): Promise<MaintenanceAlert[]> {
    const queryParams = new URLSearchParams({ daysAhead: daysAhead.toString() });
    const response = await apiClient.get(`${this.baseUrl}/maintenance-alerts?${queryParams.toString()}`);
    return response.data.data || response.data;
  }

  /**
   * Check dependencies before deletion
   */
  async checkDependencies(id: string | number): Promise<SourceGeneratorDependencies> {
    const response = await apiClient.get(`${this.baseUrl}/${id}/dependencies`);
    return response.data.data || response.data;
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const response = await apiClient.get(`${this.baseUrl}/health`);
    return response.data.data || response.data;
  }
}

// Export singleton instance
export const sourceGeneratorService = new SourceGeneratorService();
export default sourceGeneratorService;
